import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Button,
  Paper,
  Box,
  Typography,
  Link,
  Stack,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginValidationSchema } from './validate';
import { useThemeMode } from '../../utils/useThemeMode';

const LoginForm = ({ onSubmit, errorMessage }) => {
  const { mode } = useThemeMode();
  
  // React logo blue color for accents
  const reactBlue = 'rgba(97, 218, 251, 0.9)';
  
  // Dynamic form styling based on theme
  const formStyle = {
    background: mode === 'dark' ? '#0A1929' : '#fff',
    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
    borderRadius: '15px',
    padding: '30px',
    boxShadow: mode === 'dark' 
      ? `0px 0px 10px rgba(97, 218, 251, 0.2)` 
      : '0px 0px 10px rgba(0, 0, 0, 0.1)',
    border: mode === 'dark' ? `1px solid rgba(97, 218, 251, 0.1)` : 'none',
    width: '100%',
  };

  const textFieldStyle = {
    marginBottom: '20px',
    input: { 
      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit' 
    },
    label: { 
      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'inherit' 
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.3)' : undefined
      },
      "&:hover fieldset": {
        borderColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.5)' : undefined
      },
      "&.Mui-focused fieldset": {
        borderColor: mode === 'dark' ? reactBlue : undefined
      }
    }
  };

  const buttonStyle = {
    marginTop: '20px',
    padding: '10px 20px',
    fontSize: '1rem',
    backgroundColor: mode === 'dark' ? reactBlue : undefined,
    '&:hover': {
      backgroundColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.8)' : undefined
    },
    boxShadow: mode === 'dark' ? '0 4px 12px rgba(97, 218, 251, 0.25)' : undefined,
    transition: 'all 0.2s ease-in-out',
  };

  const linkStyle = {
    color: mode === 'dark' ? reactBlue : '#1976d2',
    '&:hover': {
      color: mode === 'dark' ? 'rgba(97, 218, 251, 0.8)' : '#1565c0'
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      remember: false
    },
    mode: 'onChange',
    resolver: yupResolver(loginValidationSchema)
  });

  return (
    <Box component={Paper} elevation={3} sx={formStyle}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Controller
            name="email"
            control={control}
            rules={{ 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                variant="outlined"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={textFieldStyle}
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={{ required: 'Password is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Password"
                type="password"
                variant="outlined"
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={textFieldStyle}
              />
            )}
          />

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Controller
              name="remember"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox 
                      {...field} 
                      size="small" 
                      sx={{
                        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : undefined,
                        '&.Mui-checked': {
                          color: mode === 'dark' ? reactBlue : undefined
                        }
                      }}
                    />
                  }
                  label={
                    <Typography 
                      variant="body2"
                      sx={{ color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : undefined }}
                    >
                      Remember me
                    </Typography>
                  }
                />
              )}
            />
            
            <Link 
              component={RouterLink} 
              to="/forgotpassword"
              variant="body2"
              sx={linkStyle}
            >
              Forgot password?
            </Link>
          </Box>

          {errorMessage && (
            <Typography 
              color="error" 
              align="center"
              sx={{ 
                padding: '10px',
                backgroundColor: mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(211, 47, 47, 0.05)',
                borderRadius: '4px',
                marginY: '10px'
              }}
            >
              {errorMessage}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={buttonStyle}
          >
            Login
          </Button>

          <Divider sx={{ my: 2, borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined }} />

          <Typography align="center" variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'text.secondary' }}>
            Don't have an account?{' '}
            <Link 
              component={RouterLink} 
              to="/signup"
              sx={linkStyle}
            >
              Sign up now
            </Link>
          </Typography>
        </Stack>
      </form>
    </Box>
  );
};

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  errorMessage: PropTypes.string
};

export default LoginForm;
